import { useCallback, useRef, useState } from 'react';

import { useAuth } from '../../../app/providers/useAuth';
import { useBillingStatus } from '../../billing/hooks/useBillingStatus';
import { isVoiceRecordingSupported } from '../lib/recordingSupport';
import { resolveVoiceButtonState } from '../lib/voiceGating';
import {
  AUDIO_TOO_LARGE_FAILURE,
  MAX_AUDIO_SIZE_BYTES,
  mapVoiceError,
  type VoiceFailure,
} from '../lib/voiceErrors';
import { useGrantVoiceConsent } from './useGrantVoiceConsent';
import { useVoiceRecorder, type VoiceRecording } from './useVoiceRecorder';
import { useVoiceTranscription } from './useVoiceTranscription';

// PARA-132 (AD-6/AD-8) — фасад фичи: единственное место, знающее и про запись,
// и про поле формы. Компоненты остаются презентационными.

/** Что показывает кнопка микрофона прямо сейчас. */
export type VoiceInputButtonState = 'hidden' | 'gated' | 'available' | 'recording' | 'processing';

interface UseVoiceInputOptions {
  title: string;
  setTitle: (next: string) => void;
}

export interface VoiceInputApi {
  buttonState: VoiceInputButtonState;
  elapsedSeconds: number;
  isConsentDialogOpen: boolean;
  isConsentPending: boolean;
  /** AC-E11: запись оборвал лимит длительности — объясняем, что это не сбой. */
  hasReachedLimit: boolean;
  failure: VoiceFailure | null;
  /** AC-17: откат ровно на один шаг; при пустом прежнем значении отката нет. */
  canUndo: boolean;
  /** Тап по кнопке микрофона: старт записи либо остановка текущей. */
  toggleRecording: () => void;
  cancelRecording: () => void;
  confirmConsent: () => void;
  dismissConsent: () => void;
  undo: () => void;
}

export function useVoiceInput({ title, setTitle }: UseVoiceInputOptions): VoiceInputApi {
  const { user } = useAuth();
  const { data: billingStatus } = useBillingStatus();
  // Поддержка среды не меняется в течение жизни экрана — считаем один раз.
  const [isSupported] = useState(isVoiceRecordingSupported);

  const [failure, setFailure] = useState<VoiceFailure | null>(null);
  const [previousTitle, setPreviousTitle] = useState<string | null>(null);
  const [isConsentDialogOpen, setIsConsentDialogOpen] = useState(false);
  const [hasReachedLimit, setHasReachedLimit] = useState(false);

  const plan = billingStatus?.plan;
  const hasConsent = user?.voice_consent_given_at != null;

  // Контекст ошибки и текущий title читаются в момент ответа сервера, а не в
  // момент отправки — иначе сообщение опиралось бы на устаревшее состояние.
  const errorContextRef = useRef({ plan, hasConsent });
  errorContextRef.current = { plan, hasConsent };
  const titleRef = useRef(title);
  titleRef.current = title;

  const transcription = useVoiceTranscription();
  const consent = useGrantVoiceConsent();
  const { mutate: transcribe } = transcription;
  const { mutate: grantConsent } = consent;

  const reportFailure = useCallback((error: unknown) => {
    const nextFailure = mapVoiceError(error, errorContextRef.current);
    setFailure(nextFailure);
    // AC-E2: 403 по несогласию — не сообщение об отказе, а предложение согласиться.
    if (nextFailure.kind === 'consent') {
      setIsConsentDialogOpen(true);
    }
  }, []);

  const handleRecordingComplete = useCallback(
    (recording: VoiceRecording) => {
      setHasReachedLimit(recording.reachedLimit);
      // AC-E11a: заведомо отклоняемый запрос не отправляем — квота цела.
      if (recording.blob.size > MAX_AUDIO_SIZE_BYTES) {
        setFailure(AUDIO_TOO_LARGE_FAILURE);
        return;
      }
      transcribe(
        { audio: recording.blob, mimeType: recording.mimeType },
        {
          onSuccess: ({ transcript }) => {
            // AC-6/AC-17: транскрипт ЗАМЕНЯЕТ значение, прежнее запоминается на
            // один шаг; при пустом поле откатывать не к чему (AC-17a).
            const valueBefore = titleRef.current;
            setPreviousTitle(valueBefore.length > 0 ? valueBefore : null);
            setTitle(transcript);
          },
          // AC-E5: поле формы при ошибке не трогаем вовсе.
          onError: reportFailure,
        },
      );
    },
    [reportFailure, setTitle, transcribe],
  );

  const recorder = useVoiceRecorder({
    onComplete: handleRecordingComplete,
    onError: reportFailure,
  });
  const { start: startRecording, stop: stopRecording, cancel: cancelRecording } = recorder;

  const beginRecording = useCallback(() => {
    setFailure(null);
    setHasReachedLimit(false);
    // AC-7: согласие спрашивается ДО обращения к микрофону.
    if (!hasConsent) {
      setIsConsentDialogOpen(true);
      return;
    }
    startRecording();
  }, [hasConsent, startRecording]);

  const toggleRecording = useCallback(() => {
    if (recorder.status === 'recording') {
      stopRecording();
      return;
    }
    beginRecording();
  }, [beginRecording, recorder.status, stopRecording]);

  const confirmConsent = useCallback(() => {
    grantConsent(undefined, {
      onSuccess: () => {
        setIsConsentDialogOpen(false);
        setFailure(null);
        startRecording();
      },
      onError: (error) => {
        // EC-21: запись не стартует, профиль не изменён.
        setIsConsentDialogOpen(false);
        setFailure(mapVoiceError(error, errorContextRef.current));
      },
    });
  }, [grantConsent, startRecording]);

  // AC-9: отказ от согласия — ни запроса, ни записи; форма остаётся рабочей.
  const dismissConsent = useCallback(() => setIsConsentDialogOpen(false), []);

  const undo = useCallback(() => {
    if (previousTitle === null) {
      return;
    }
    setTitle(previousTitle);
    setPreviousTitle(null);
  }, [previousTitle, setTitle]);

  const gatingState = resolveVoiceButtonState({ isSupported, plan });
  let buttonState: VoiceInputButtonState = gatingState;
  if (recorder.status === 'recording') {
    buttonState = 'recording';
  } else if (transcription.isPending) {
    buttonState = 'processing';
  }

  return {
    buttonState,
    elapsedSeconds: recorder.elapsedSeconds,
    isConsentDialogOpen,
    isConsentPending: consent.isPending,
    hasReachedLimit,
    failure,
    canUndo: previousTitle !== null,
    toggleRecording,
    cancelRecording,
    confirmConsent,
    dismissConsent,
    undo,
  };
}
